import { trackErrorEvent } from "@/utils/analytics";
import type { Job } from "@/utils/models";
import { StaffingTotalsMode } from "./monthlyJobUtils";

/**
 * Per-user / per-department preferences for the Monthly staffing view (APP-465), persisted to
 * localStorage. Kept client-side for v0: the PRD's department-level "admin selects one mode" belongs
 * in a backend department setting, tracked as a follow-up. Every read/write is guarded so a private
 * window or blocked storage degrades to defaults instead of throwing.
 */

export type StaffingViewChoice = "weekly" | "monthly";

const LAST_VIEW_KEY = "staffing_last_view";
const CONFIG_KEY_PREFIX = "monthly_view_config";

const configKey = (departmentId: string): string => `${CONFIG_KEY_PREFIX}:${departmentId}`;

/** Job display names shown by default when the user has no saved selection (PRD: Nurse + Aide only). */
const DEFAULT_VISIBLE_JOB_NAMES = ["nurse", "aide"];

export type MonthlyViewConfig = {
	totalsMode: StaffingTotalsMode;
	/** Job ids the user chose to display; `null` means "not chosen yet" → fall back to the default set. */
	visibleJobIds: string[] | null;
};

export const readLastStaffingView = (): StaffingViewChoice | null => {
	try {
		const raw = localStorage.getItem(LAST_VIEW_KEY);
		return raw === "weekly" || raw === "monthly" ? raw : null;
	} catch (error) {
		trackErrorEvent("monthly_view_read_last_view_failed", { error: String(error) });
		return null;
	}
};

export const writeLastStaffingView = (view: StaffingViewChoice): void => {
	try {
		localStorage.setItem(LAST_VIEW_KEY, view);
	} catch (error) {
		trackErrorEvent("monthly_view_write_last_view_failed", { error: String(error) });
	}
};

export const readMonthlyViewConfig = (departmentId: string): MonthlyViewConfig => {
	try {
		const raw = localStorage.getItem(configKey(departmentId));
		if (!raw) return { totalsMode: StaffingTotalsMode.ByJob, visibleJobIds: null };
		const parsed = JSON.parse(raw) as Partial<MonthlyViewConfig>;
		const totalsMode = Object.values(StaffingTotalsMode).includes(parsed.totalsMode as StaffingTotalsMode)
			? (parsed.totalsMode as StaffingTotalsMode)
			: StaffingTotalsMode.ByJob;
		const visibleJobIds = Array.isArray(parsed.visibleJobIds) ? parsed.visibleJobIds.filter((id): id is string => typeof id === "string") : null;
		return { totalsMode, visibleJobIds };
	} catch (error) {
		trackErrorEvent("monthly_view_read_config_failed", { departmentId, error: String(error) });
		return { totalsMode: StaffingTotalsMode.ByJob, visibleJobIds: null };
	}
};

export const writeMonthlyViewConfig = (departmentId: string, config: MonthlyViewConfig): void => {
	try {
		localStorage.setItem(configKey(departmentId), JSON.stringify(config));
	} catch (error) {
		trackErrorEvent("monthly_view_write_config_failed", { departmentId, error: String(error) });
	}
};

/**
 * The jobs shown as pills: the user's saved selection (intersected with the department's currently
 * enabled jobs), or — when nothing is saved — the default Nurse/Aide set, falling back to every
 * enabled job when neither name is present.
 */
export const resolveVisibleJobs = (enabledJobs: Job[], savedVisibleJobIds: string[] | null): Job[] => {
	if (savedVisibleJobIds && savedVisibleJobIds.length > 0) {
		const chosen = new Set(savedVisibleJobIds);
		const resolved = enabledJobs.filter((job) => chosen.has(job.id));
		if (resolved.length > 0) return resolved;
	}
	const defaults = enabledJobs.filter((job) => DEFAULT_VISIBLE_JOB_NAMES.includes(job.displayName.trim().toLowerCase()));
	// DEFAULT: no Nurse/Aide job configured → show every enabled job rather than an empty grid.
	return defaults.length > 0 ? defaults : enabledJobs;
};
